import React from 'react';
import { Box, Heading, Text, HStack, VStack, Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, Button } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const InvoiceDetail = () => {
  const { id } = useParams();
  const { t, currentLanguage } = useLanguage();
  const { formatCurrency } = useCurrency();

  const { data: invoice, isLoading } = useQuery(['invoice', id], () => api.get(`/api/invoices/${id}`).then(r => r.data.data.invoice));
  const { data: items } = useQuery(['invoice_items', id], () => api.get(`/api/invoices/${id}/items`).then(r => r.data.data.items));
  const { data: payments } = useQuery(['invoice_payments', id], () => api.get(`/api/invoices/${id}/payments`).then(r => r.data.data.payments));

  if (isLoading) return <Box p={4}>{t('common.loading')||'Loading...'}</Box>;
  if (!invoice) return <Box p={4}>{t('messages.not_found')||'Not found'}</Box>;

  const totalPaid = (payments || []).reduce((s,p)=> s + Number(p.amount||0), 0);
  const invAmount = Number(invoice.invoiced_amount || invoice.total_amount || 0);
  const remaining = Math.max(0, invAmount - totalPaid);

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">{t('invoices.title')||'Invoice'} #{invoice.invoice_number}</Heading>
        <HStack>
          <Button size="sm" onClick={async ()=>{
            try {
              const res = await api.get(`/api/invoices/${invoice.id}/pdf?lang=${currentLanguage}`, { responseType: 'blob' });
              const blob = new Blob([res.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              window.open(url, '_blank');
              setTimeout(()=> window.URL.revokeObjectURL(url), 10000);
            } catch (e) {}
          }}>{t('common.export_pdf')||'PDF'}</Button>
        </HStack>
      </HStack>

      <HStack align="start" spacing={6} mb={6}>
        <Card flex={1}><CardBody>
          <VStack align="start" spacing={1}>
            <Text><b>{t('invoices.customer')||'Customer'}:</b> {invoice.client_name}</Text>
            <Text><b>{t('invoices.type')||'Type'}:</b> {invoice.invoice_type}</Text>
            <Text><b>{t('invoices.status')||'Status'}:</b> {invoice.status}</Text>
            <Text><b>{t('invoices.date')||'Date'}:</b> {new Date(invoice.created_at).toLocaleDateString()}</Text>
            <Text><b>{t('invoices.amount')||'Amount'}:</b> {formatCurrency(invAmount, invoice.currency)}</Text>
          </VStack>
        </CardBody></Card>
        <Card flex={1}><CardBody>
          <VStack align="start" spacing={1}>
            <Text><b>{t('payments.title')||'Payments'}:</b> {formatCurrency(totalPaid, invoice.currency)}</Text>
            <Text><b>{t('invoices.outstanding_balance')||'Outstanding'}:</b> {formatCurrency(remaining, invoice.currency)}</Text>
          </VStack>
        </CardBody></Card>
      </HStack>

      <Card mb={6}><CardBody>
        <Heading size="sm" mb={3}>{t('invoices.items')||'Items'}</Heading>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{t('table.description')||'Description'}</Th>
              <Th isNumeric>{t('table.qty')||'Qty'}</Th>
              <Th isNumeric>{t('table.unit_price')||'Unit Price'}</Th>
              <Th isNumeric>{t('table.final_price')||'Total'}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(items||[]).map(it => (
              <Tr key={it.id}>
                <Td>{it.item_name}</Td>
                <Td isNumeric>{it.quantity}</Td>
                <Td isNumeric>{Number(it.unit_price||0).toFixed(2)}</Td>
                <Td isNumeric>{Number(it.total_price||0).toFixed(2)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody></Card>

      <Card><CardBody>
        <Heading size="sm" mb={3}>{t('payments.title')||'Payments'}</Heading>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{t('payments.date')||'Date'}</Th>
              <Th>{t('payments.method')||'Method'}</Th>
              <Th isNumeric>{t('payments.amount')||'Amount'}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(payments||[]).map(p => (
              <Tr key={p.id}>
                <Td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '-'}</Td>
                <Td>{p.method || '-'}</Td>
                <Td isNumeric>{Number(p.amount||0).toFixed(2)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody></Card>
    </Box>
  );
};

export default InvoiceDetail;


